import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, Pencil, Trash2, Plus } from "lucide-react";

const EMPTY_FORM = {
    label: "",
    stripe_product_id: "",
    stripe_price_id: "",
    stripe_coupon_id: "",
    notes: "",
    active: true,
};

export default function AdminStripeProducts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/stripe-products");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const stats = useMemo(() => {
        const active = rows.filter((r) => r.active).length;
        const withCoupon = rows.filter((r) => r.stripe_coupon_id).length;
        return { total: rows.length, active, withCoupon };
    }, [rows]);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            label: row.label || "",
            stripe_product_id: row.stripe_product_id || "",
            stripe_price_id: row.stripe_price_id || "",
            stripe_coupon_id: row.stripe_coupon_id || "",
            notes: row.notes || "",
            active: row.active ?? true,
        });
        setDialogOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                label: form.label.trim(),
                stripe_product_id: form.stripe_product_id.trim(),
                stripe_price_id: form.stripe_price_id.trim(),
                stripe_coupon_id: form.stripe_coupon_id.trim() || null,
                notes: form.notes?.trim() || null,
                active: form.active,
            };
            if (editing) {
                await api.patch(`/admin/stripe-products/${editing.id}`, payload);
                toast.success(`Config "${payload.label}" updated`);
            } else {
                await api.post("/admin/stripe-products", payload);
                toast.success(`Config "${payload.label}" created`);
            }
            setDialogOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (
            !window.confirm(
                `Delete Stripe config "${row.label}"? This cannot be undone.`
            )
        )
            return;
        try {
            await api.delete(`/admin/stripe-products/${row.id}`);
            toast.success("Config deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Payments"
                title="Stripe products"
                description="Ops-configured mappings from a Perx GUID to a concrete Stripe Product + Price (+ optional Coupon). Products can reference one of these instead of auto-generating a lookup key."
                testid="admin-stripe-header"
                action={
                    <Button
                        onClick={openNew}
                        data-testid="stripe-new-btn"
                        className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold h-11 px-5"
                    >
                        <Plus size={16} className="mr-2" /> New Stripe config
                    </Button>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <StatCard label="Total configs" value={stats.total} testid="stripe-stat-total" />
                    <StatCard label="Active" value={stats.active} testid="stripe-stat-active" />
                    <StatCard
                        label="With coupon"
                        value={stats.withCoupon}
                        accent
                        testid="stripe-stat-coupon"
                    />
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="stripe-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Label
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Stripe Product
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Stripe Price
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Coupon
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Status
                                </TableHead>
                                <TableHead className="text-right" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-navy-500 py-10">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-navy-500 py-10">
                                        <div className="flex flex-col items-center gap-2">
                                            <CreditCard className="text-gold-500" />
                                            <div>No Stripe configs yet. Create one to reuse a Stripe Price across products.</div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`stripe-row-${r.id}`}>
                                    <TableCell className="text-navy-700 font-medium">
                                        {r.label}
                                        <div className="text-[11px] text-navy-400 font-mono mt-0.5">
                                            {r.id}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-500 font-mono text-xs">
                                        {r.stripe_product_id}
                                    </TableCell>
                                    <TableCell className="text-navy-500 font-mono text-xs">
                                        {r.stripe_price_id}
                                    </TableCell>
                                    <TableCell className="text-navy-500 font-mono text-xs">
                                        {r.stripe_coupon_id || "—"}
                                    </TableCell>
                                    <TableCell>
                                        {r.active ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 border-0">
                                                ACTIVE
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-navy-50 text-navy-500 border-0">
                                                PAUSED
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`stripe-edit-${r.id}`}
                                                onClick={() => openEdit(r)}
                                                className="text-navy-700 hover:text-navy-900"
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`stripe-delete-${r.id}`}
                                                onClick={() => remove(r)}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="stripe-dialog" className="max-w-lg bg-white">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            {editing ? `Edit "${editing.label}"` : "New Stripe config"}
                        </DialogTitle>
                        <DialogDescription>
                            Paste the IDs from your Stripe dashboard. The Perx GUID is
                            generated automatically and is what products reference.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4">
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Label
                            </Label>
                            <Input
                                value={form.label}
                                data-testid="stripe-form-label"
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="e.g. HR Premium Insurance"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Stripe product ID
                            </Label>
                            <Input
                                value={form.stripe_product_id}
                                data-testid="stripe-form-product-id"
                                onChange={(e) =>
                                    setForm({ ...form, stripe_product_id: e.target.value })
                                }
                                placeholder="prod_1abc…"
                                className="mt-1 font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Stripe price ID
                            </Label>
                            <Input
                                value={form.stripe_price_id}
                                data-testid="stripe-form-price-id"
                                onChange={(e) =>
                                    setForm({ ...form, stripe_price_id: e.target.value })
                                }
                                placeholder="price_1abc…"
                                className="mt-1 font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Stripe coupon ID (optional)
                            </Label>
                            <Input
                                value={form.stripe_coupon_id}
                                data-testid="stripe-form-coupon-id"
                                onChange={(e) =>
                                    setForm({ ...form, stripe_coupon_id: e.target.value })
                                }
                                placeholder="coupon_1abc…"
                                className="mt-1 font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Notes
                            </Label>
                            <Textarea
                                value={form.notes}
                                data-testid="stripe-form-notes"
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                rows={2}
                                className="mt-1"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                checked={form.active}
                                data-testid="stripe-form-active"
                                onCheckedChange={(v) => setForm({ ...form, active: v })}
                            />
                            <div>
                                <div className="text-sm text-navy-700 font-medium">Active</div>
                                <div className="text-xs text-navy-500">
                                    Only active configs can be used on new checkouts.
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            data-testid="stripe-form-cancel"
                            onClick={() => setDialogOpen(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={
                                saving ||
                                !form.label.trim() ||
                                !form.stripe_product_id.trim() ||
                                !form.stripe_price_id.trim()
                            }
                            data-testid="stripe-form-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                        >
                            {saving ? "Saving…" : editing ? "Save changes" : "Create config"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function StatCard({ label, value, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl shadow-soft p-6 ${
                accent ? "bg-navy-700 text-white" : "bg-white"
            }`}
        >
            <div
                className={`text-xs uppercase tracking-[0.3em] mb-2 ${
                    accent ? "text-gold-500" : "text-gold-600"
                }`}
            >
                {label}
            </div>
            <div className={`font-display text-4xl ${accent ? "" : "text-navy-700"}`}>
                {value}
            </div>
        </div>
    );
}
